import React, { createContext, useContext, useState, useEffect } from 'react';
import { flushSync } from 'react-dom';

const IMPERSONATION_KEYS = [
    'impersonation_admin_token',
    'impersonation_admin_refresh_token',
    'impersonation_admin_token_expiry',
    'impersonation_admin_user_detail',
];

const clearImpersonationBackup = () => {
    IMPERSONATION_KEYS.forEach((k) => sessionStorage.removeItem(k));
};

// Creating the AuthContext
const AuthContext = createContext();

// Custom hook to access authentication context
export const useAuth = () => {
    return useContext(AuthContext);
};

// The Authentication Provider
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);

    // Load user details
    useEffect(() => {
        const token = sessionStorage.getItem('token');
        const userDetail = sessionStorage.getItem('user_detail');

        // If there's a token and user details in sessionStorage, we restore the user state
        if (token && userDetail) {
            const userDetailParsed = JSON.parse(userDetail);
            setUser({ email: userDetailParsed.email, role: userDetailParsed.user_type });
        }
    }, []);

    const login = async (email, password, toast) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/login`, { 
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            });
    
            const data = await response.json();

            //Check if user login or not
            if(data.status === true){
                clearImpersonationBackup();
                // Get Values and store in variables
                const token = data.data.token || null;
                const refresh_token = data.data.refresh_token || null;
                const token_expiry = data.data.token_expiry || null;
                const user_detail = data.data.user_detail || null;
        
                const { user_type } = user_detail;
        
                // Store token and user details in sessionStorage
                sessionStorage.setItem('token', token);
                sessionStorage.setItem('refresh_token', refresh_token);
                sessionStorage.setItem('token_expiry', token_expiry);
                sessionStorage.setItem('user_detail', JSON.stringify(user_detail));
        
                // Set user state
                setUser({ email, role: user_type });

                return user_type;
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            throw new Error(error.message || 'Login failed.');
        }
    };

    const loginWithGoogle = async (googleToken, toast) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/login-with-google`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token: googleToken }),
            });
    
            const data = await response.json();
    
            if (data.status === true) {
                clearImpersonationBackup();
                const { token, refresh_token, token_expiry, user_detail } = data.data;
                const { user_type } = user_detail;
    
                sessionStorage.setItem('token', token);
                sessionStorage.setItem('refresh_token', refresh_token);
                sessionStorage.setItem('token_expiry', token_expiry);
                sessionStorage.setItem('user_detail', JSON.stringify(user_detail));
    
                // Make sure setUser is defined in your AuthContext
                if (typeof setUser === 'function') {
                    setUser({
                        email: user_detail.email,
                        role: user_type,
                        ...user_detail,
                    });
                }
    
                return user_type;
            } else {
                toast.error(data.message || 'Google login failed');
                return null;
            }
        } catch (error) {
            toast.error(error.message || 'Google login failed');
            return null;
        }
    };

    const logout = (navigate) => {
        clearImpersonationBackup();
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('token_expiry');
        sessionStorage.removeItem('user_detail');
        setUser(null);
        navigate('/login');
    };

    /** Admin is viewing a customer account; restores admin session on exit. */
    const startImpersonationSession = (apiData, navigate, redirectPath = '/customer/apps/google-reviews') => {
        const d = apiData?.data || apiData;
        if (!d?.token || !d?.user_detail) {
            return false;
        }
        if (!sessionStorage.getItem('impersonation_admin_token')) {
            sessionStorage.setItem('impersonation_admin_token', sessionStorage.getItem('token') || '');
            sessionStorage.setItem('impersonation_admin_refresh_token', sessionStorage.getItem('refresh_token') || '');
            sessionStorage.setItem('impersonation_admin_token_expiry', sessionStorage.getItem('token_expiry') || '');
            sessionStorage.setItem('impersonation_admin_user_detail', sessionStorage.getItem('user_detail') || '');
        }
        sessionStorage.setItem('token', d.token);
        sessionStorage.setItem('refresh_token', d.refresh_token || '');
        sessionStorage.setItem('token_expiry', d.token_expiry || '');
        sessionStorage.setItem('user_detail', JSON.stringify(d.user_detail));
        const { email, user_type } = d.user_detail;
        flushSync(() => {
            setUser({ email, role: user_type });
        });
        const normalizedPath = String(redirectPath || '').trim();
        const path = normalizedPath.startsWith('/customer/')
            ? normalizedPath
            : '/customer/apps/google-reviews';
        navigate(path, { replace: true });
        return true;
    };

    const exitImpersonationSession = (navigate) => {
        const backupToken = sessionStorage.getItem('impersonation_admin_token');
        if (!backupToken) return;
        const backupRefresh = sessionStorage.getItem('impersonation_admin_refresh_token') || '';
        const backupExpiry = sessionStorage.getItem('impersonation_admin_token_expiry') || '';
        const backupDetail = sessionStorage.getItem('impersonation_admin_user_detail') || '';

        sessionStorage.setItem('token', backupToken);
        sessionStorage.setItem('refresh_token', backupRefresh);
        sessionStorage.setItem('token_expiry', backupExpiry);
        if (backupDetail) {
            sessionStorage.setItem('user_detail', backupDetail);
        }
        clearImpersonationBackup();

        /* Apply admin role before changing URL — otherwise customer routes still match /admin/all-customers → wrong page. */
        try {
            const parsed = JSON.parse(backupDetail || '{}');
            flushSync(() => {
                setUser({ email: parsed.email, role: parsed.user_type });
            });
        } catch {
            flushSync(() => setUser(null));
        }
        navigate('/admin/all-customers', { replace: true });
    };

    const isImpersonating = () => !!sessionStorage.getItem('impersonation_admin_token');

    return (
        <AuthContext.Provider
            value={{
                user,
                login,
                loginWithGoogle,
                logout,
                startImpersonationSession,
                exitImpersonationSession,
                isImpersonating,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
